/**
 * `Products` renders the grid/list of product cards on the product page.
 *
 * Responsibilities:
 * - Maps `products` into `Product` cards.
 * - Looks up per-product CMS customisation from `cms.productTypeCustomisation`
 *   (synopsis text, key benefits, "see more" items, GAP cool-off warning text).
 * - Computes quoted prices (when `quoteProducts` are supplied) and controls
 *   whether to show quoted pricing per card.
 * - Disables products that share a selected/purchased parent product type.
 * - Applies a 1-day cool-off period rule for GAP quotes based on `createdOn`.
 */
import React from 'react';
import {
  KeyBenefitList,
  ProductPageCustomisation,
  ProductType,
  QuoteProduct
} from '../CommonTypes';
import Product from './Product';
import { getProductIdMatchIfExists, getQuotedPrice } from '../ProductMethods';
import { ProductTypes } from '../../../consts/Common';

type ProductsProps = {
  products: ProductType[];
  cms: ProductPageCustomisation;
  quoteProducts?: QuoteProduct[];
  addOrRemoveProduct: (productTypeCode: string) => void;
  culture: string;
  showKeyBenefits: boolean;
  showQuotedPrice: boolean;
  showKeyBenefitsInProductCard?: boolean;
};

const Products = ({
  products,
  cms,
  quoteProducts,
  addOrRemoveProduct,
  culture,
  showKeyBenefits,
  showQuotedPrice,
  showKeyBenefitsInProductCard
}: ProductsProps): JSX.Element => {
  const selectedParentIds = products
    .filter((p) => p.isSelected || p.isPurchased)
    .map((p) => p.parentProductTypeId);

  const hasLinkedRoadside = (product: ProductType): boolean =>
    product.productVariants.some((v) =>
      v.linkedProducts.some(
        (lp) => lp.typeId === ProductTypes.RoadsideAssistance
      )
    );

  const productWithLinkedRoadsideSelected = products.some(
    (p) => (p.isSelected || p.isPurchased) && hasLinkedRoadside(p)
  );

  const standaloneRoadsideSelected = products.some(
    (p) =>
      (p.isSelected || p.isPurchased) &&
      p.productTypeId === ProductTypes.RoadsideAssistance
  );

  const isMutuallyExcluded = (product: ProductType): boolean => {
    if (
      product.productTypeId === ProductTypes.RoadsideAssistance &&
      productWithLinkedRoadsideSelected
    ) {
      return true;
    }
    if (hasLinkedRoadside(product) && standaloneRoadsideSelected) {
      return true;
    }
    return false;
  };

  const getCustomisationByProduct = (product: ProductType) => {
    return cms.productTypeCustomisation.find((p) => {
      const { id } = getProductIdMatchIfExists(product, p);
      return !!id;
    });
  };

  const getShowMoreItems = (product: ProductType) => {
    const showMoreItems: KeyBenefitList[] = [];

    const productCustomisation = getCustomisationByProduct(product);

    const showMoreItem: KeyBenefitList = {
      title: productCustomisation?.name || '',
      listId: product.productTypeId,
      keyBenefits: productCustomisation?.seeMoreItems || []
    };

    showMoreItems.push(showMoreItem);

    return showMoreItems;
  };

  return (
    <div className='form'>
      <div className='flex-row'>
        {products.map((product: ProductType, i) => {
          const quotePrice = getQuotedPrice(product, quoteProducts ?? []);

          const isGapProductInCooloffPeriod = () => {
            if (product.productTypeId === ProductTypes.Gap && quoteProducts) {
              const gapQuoteProduct = quoteProducts.find(
                (qp) => qp.productTypeId === product.productTypeId
              );

              if (gapQuoteProduct?.createdOn) {
                const gapDateTime = new Date(gapQuoteProduct.createdOn);

                const coolOffDateTime = gapDateTime.setDate(
                  gapDateTime.getDate() + 1
                );

                const dateTimeNow = new Date().getTime();

                return dateTimeNow < coolOffDateTime;
              }
            }

            return false;
          };

          return (
            <div key={`product-${i}`} className='flex-column'>
              <Product
                key={`product-${i}`}
                id={product.productTypeCode}
                title={product.productName}
                isSelected={product.isSelected}
                addOrRemoveProduct={addOrRemoveProduct}
                showMoreItems={getShowMoreItems(product)}
                synopsisText={getCustomisationByProduct(product)?.synopsisText}
                showQuotedPrice={showQuotedPrice && quotePrice !== undefined}
                noOfInstallments={quotePrice?.noOfInstallments}
                paymentMethod={quotePrice?.paymentMethod}
                annualPrice={quotePrice?.totalPrice}
                monthlyPrice={quotePrice?.monthlyPrice}
                isPurchased={product.isPurchased}
                isDisabled={
                  selectedParentIds.some(
                    (cl) => cl == product.parentProductTypeId
                  ) || isMutuallyExcluded(product)
                }
                showCooloffWarning={isGapProductInCooloffPeriod()}
                cooloffWarningText={
                  getCustomisationByProduct(product)?.gapCoolOffWarning
                }
                culture={culture}
                showKeyBenefits={showKeyBenefits}
                showKeyBenefitsInProductCard={
                  getCustomisationByProduct(product)
                    ?.showKeyBenefitsInProductCard &&
                  showKeyBenefitsInProductCard
                }
                hideRemovePlanLink={products.length === 1}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Products;
